// ==================================================================================
// Digital Approval System (DAS) - Role Permissions Config
// ==================================================================================
// The frontend's single source of truth for:
//   - The catalog of UI modules that can be permission-gated (ALL_MODULES)
//   - The catalog of action codes (ALL_ACTIONS)
//   - Helpers that merge multiple role-permission rows into one effective
//     visible-modules + module-actions map, and that filter the sidebar nav.
//
// Module IDs MUST match the keys used in main_layout.tsx ENTERPRISE_LAYOUT_CONFIG.modules
// and the role-permission JSONB seed in admin.tbl_role_permission.
// Action codes MUST match what each module's UI uses for button-visibility checks.
// ==================================================================================

import type {
  RolePermission,
  RolePermissionMerged,
} from 'api/types/admin/rolePermissions.types';

// ---------------------------------------------------------------------------
// Catalogs
// ---------------------------------------------------------------------------

/**
 * Every UI module that can be permission-gated. Each entry declares:
 *   - `id`       : the module identifier used in main_layout.tsx + role-permission JSONB
 *   - `label`    : display label (mirrors the sidebar nav title verbatim)
 *   - `section`  : the sidebar section the module appears under — used by the
 *                  role-permission modal to group checkboxes the same way the
 *                  user sees them in the left menu
 *   - `actions`  : per-module catalog of applicable action codes
 *
 * If you add a new screen to the sidebar, also add an entry here so it can
 * be granted to roles via the admin UI. Keep `label` + `section` in sync with
 * main_layout.tsx ENTERPRISE_LAYOUT_CONFIG.navigation.
 */
export const ALL_MODULES: ReadonlyArray<{
  id: string;
  label: string;
  section: string;
  actions: ReadonlyArray<string>;
}> = [
  // ── My Workspace ────────────────────────────────────────────────
  // NOTE: `das-dashboard` (Requests & Approvals) is intentionally NOT in
  // this catalog. It is the core working area accessible to any user with
  // DAS app access and is always shown in the sidebar via the
  // ALWAYS_VISIBLE_ITEMS list in main_layout.tsx — no per-role gate.
  //
  // `actions` here is the catalog of in-screen buttons that need gating.
  // It deliberately omits `view` — opening the menu item IS the view, and
  // a separate `view` checkbox would be redundant noise in the admin UI.
  // `view-all` on `request-status-lookup` controls scope: when granted,
  // the user sees ALL requests on Track Request; when not granted, they
  // see only requests where they are submitter / creator / approver /
  // delegate / escalation target / role-based approver. Module visibility
  // (whether the menu item appears) is still gated by visible_modules
  // alone; this action only widens the row scope inside the screen.
  //
  // `edit-external-ref` controls who can edit a request's External
  // Reference Number from the Track Request screen. Was previously
  // hardcoded to das_admin on the backend [Authorize(Roles=…)] and on
  // the frontend isAdmin gate; both are now removed in favour of this
  // permission so admins can grant it to any role via the Role
  // Permissions UI.
  { id: 'request-status-lookup',         label: 'Track Request',          section: 'My Workspace',   actions: ['view-all', 'edit-external-ref', 'export'] },

  // ── Administration ─────────────────────────────────────────────
  { id: 'request-types-management',      label: 'Request Types',          section: 'Administration', actions: ['add', 'edit', 'delete'] },
  { id: 'user-management',               label: 'User Management',        section: 'Administration', actions: ['add', 'edit', 'delete', 'reset-password'] },
  { id: 'role-permissions-management',   label: 'Role Permissions',       section: 'Administration', actions: ['add', 'edit', 'delete'] },

  // ── My Reports ─────────────────────────────────────────────────
  { id: 'report-my-workflow-summary',    label: 'Workflow Summary',       section: 'My Reports',     actions: ['export'] },

  // ── Reports ────────────────────────────────────────────────────
  // The five request-lifecycle report screens are always visible (see
  // ALWAYS_VISIBLE_ITEMS in main_layout.tsx). `reports` is a single logical
  // grouping whose `view-all` action controls DATA SCOPE for all of them:
  // when granted, the report shows ALL users' requests; otherwise the API
  // scopes rows to the caller's own flow. Matches the backend
  // ReportsController check (module `reports`, action `view-all`).
  { id: 'reports',                       label: 'Reports (all data)',     section: 'Reports',        actions: ['view-all'] },
];

/**
 * The ordered list of section headers under which `ALL_MODULES` rolls up.
 * Order here drives the render order in the role-permission modal.
 */
export const MODULE_SECTIONS: ReadonlyArray<string> = [
  'My Workspace',
  'Administration',
  'My Reports',
  'Reports',
];

/**
 * All known action codes. Per-module applicability lives in
 * <code>ALL_MODULES[*].actions</code>; this is the master list.
 */
// Master list of action codes used across `ALL_MODULES`.
// Does NOT include `view` — opening a screen IS the view; sidebar
// visibility (visible_modules) is the gate, not a separate action.
export const ALL_ACTIONS = [
  'add',
  'edit',
  'delete',
  'export',
  'reset-password',
  // `view-all` widens scope from "my involved records" to "all records"
  // on a list/lookup screen. Currently used by `request-status-lookup`;
  // can be reused on any future screen that has the same admin-vs-user
  // visibility split.
  'view-all',
  // `edit-external-ref` gates editing of the External Reference Number
  // on a request. Currently used by `request-status-lookup`.
  'edit-external-ref',
] as const;

export type ActionCode = typeof ALL_ACTIONS[number];

/**
 * Seeded role codes — mirrors admin.tbl_role_permission seed data.
 */
export const ROLES = {
  SYSTEM:   'das_system',
  ADMIN:    'das_admin',
  APPROVER: 'das_approver',
  USER:     'das_user',
} as const;

/**
 * The frontend login flow rejects users whose JWT carries none of these
 * codes (the login role-gate). Matches the LMMS pattern.
 */
export const VALID_DAS_ROLES: ReadonlyArray<string> = Object.values(ROLES);

/**
 * Top-level super-role. Two invariants:
 *   1. Always granted the FULL catalog of modules + actions (computed
 *      dynamically from ALL_MODULES — so adding a new screen automatically
 *      grants it without an admin re-editing the row).
 *   2. Cannot be modified via the UI — the role-permission modal renders
 *      it read-only and the row's Edit/Delete actions are hidden.
 */
export const SUPER_ROLE_CODE = ROLES.SYSTEM;

/**
 * Build the merged permissions payload for the super-role: every module
 * the frontend knows about, with every action that module declares.
 */
export function buildFullPermissionsFromCatalog(): RolePermissionMerged {
  const visibleModules: string[] = [];
  const moduleActions: Record<string, string[]> = {};
  for (const m of ALL_MODULES) {
    visibleModules.push(m.id);
    moduleActions[m.id] = [...m.actions];
  }
  return {
    visibleModules: [...visibleModules].sort(),
    moduleActions: Object.fromEntries(
      Object.entries(moduleActions).map(([k, v]) => [k, [...v].sort()])
    ),
  };
}

/**
 * Returns true when any of the supplied role codes is the super-role.
 */
export function isSuperRole(roleCodes: ReadonlyArray<string>): boolean {
  return roleCodes.includes(SUPER_ROLE_CODE);
}

// ---------------------------------------------------------------------------
// Merge / filter helpers
// ---------------------------------------------------------------------------

/**
 * Set-union of visible modules + per-module action codes across the supplied
 * role-permission rows. Pure helper — no I/O. Used when the frontend has
 * already fetched the rows; for the at-login case use
 * <code>rolePermissionsService.getByRoles()</code> which calls the backend
 * <code>/by-roles</code> endpoint and returns the same shape.
 */
export function mergeRolePermissions(rolePerms: RolePermission[]): RolePermissionMerged {
  const visibleModules = new Set<string>();
  const moduleActions: Record<string, Set<string>> = {};

  for (const rp of rolePerms ?? []) {
    for (const m of rp.visibleModules ?? []) {
      if (m) visibleModules.add(m);
    }
    for (const [moduleId, actions] of Object.entries(rp.moduleActions ?? {})) {
      if (!moduleActions[moduleId]) moduleActions[moduleId] = new Set<string>();
      for (const a of actions ?? []) {
        if (a) moduleActions[moduleId].add(a);
      }
    }
  }

  return {
    visibleModules: [...visibleModules].sort(),
    moduleActions: Object.fromEntries(
      Object.entries(moduleActions).map(([k, v]) => [k, [...v].sort()])
    ),
  };
}

/**
 * Returns TRUE if the user's merged permissions grant <code>action</code>
 * inside <code>moduleId</code>.
 */
export function hasAction(
  moduleId: string,
  action: string,
  merged: RolePermissionMerged | undefined | null
): boolean {
  if (!merged) return false;
  const actions = merged.moduleActions?.[moduleId];
  return Array.isArray(actions) && actions.includes(action);
}

/**
 * Returns TRUE if the module is in the user's visible-modules set.
 * <code>system</code> roles get a free pass via the empty-permissions case;
 * the auth gate above ensures we never see a logged-in user with NO roles.
 */
export function canSeeModule(
  moduleId: string,
  merged: RolePermissionMerged | undefined | null
): boolean {
  if (!merged) return false;
  return Array.isArray(merged.visibleModules) && merged.visibleModules.includes(moduleId);
}
